(function () {
  const AUTOPLAY_MS = 2400;
  const RESUME_AFTER_INTERACTION_MS = 3200;
  const SECTION_SELECTOR = '.about-us-live-performance';
  const FEATURES_SELECTOR = '.about-us-live-performance__features';
  const FEATURE_SELECTOR = '.about-us-live-performance__feature';
  const SLIDER_CLASS = 'about-us-live-performance__features--slider';

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function isMobileLayout() {
    return window.matchMedia && window.matchMedia('(max-width: 749px)').matches;
  }

  class AboutUsLivePerformanceSlider {
    constructor(section) {
      this.section = section;
      this.features = section.querySelector(FEATURES_SELECTOR);
      this.items = this.features ? Array.from(this.features.querySelectorAll(FEATURE_SELECTOR)) : [];
      this.autoplayTimer = null;
      this.resumeTimer = null;
      this.isVisible = true;
      this.activeIndex = 0;
      this.scrollEndTimer = null;

      if (!this.features || this.items.length < 2) return;

      this.handleResize = this.handleResize.bind(this);
      this.handleScroll = this.handleScroll.bind(this);
      this.pauseAndScheduleResume = this.pauseAndScheduleResume.bind(this);

      this.bindEvents();
      this.syncMode();
    }

    bindEvents() {
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.handleResize());
        this.resizeObserver.observe(this.section);
        this.resizeObserver.observe(this.features);
      } else {
        window.addEventListener('resize', this.handleResize);
      }

      this.features.addEventListener('scroll', this.handleScroll, { passive: true });
      this.features.addEventListener('mouseenter', () => this.stopAutoplay());
      this.features.addEventListener('mouseleave', () => this.maybeStartAutoplay());
      this.features.addEventListener('focusin', () => this.stopAutoplay());
      this.features.addEventListener('focusout', () => this.maybeStartAutoplay());
      this.features.addEventListener('pointerdown', this.pauseAndScheduleResume);
      this.features.addEventListener('touchstart', this.pauseAndScheduleResume, { passive: true });
      this.features.addEventListener('wheel', this.pauseAndScheduleResume, { passive: true });

      if ('IntersectionObserver' in window) {
        this.visibilityObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            this.isVisible = entry.isIntersecting;
            if (this.isVisible) this.maybeStartAutoplay();
            else this.stopAutoplay();
          });
        });
        this.visibilityObserver.observe(this.section);
      }
    }

    handleResize() {
      window.requestAnimationFrame(() => this.syncMode());
    }

    handleScroll() {
      clearTimeout(this.scrollEndTimer);
      this.scrollEndTimer = window.setTimeout(() => {
        this.activeIndex = this.getNearestIndex();
      }, 120);
    }

    pauseAndScheduleResume() {
      this.stopAutoplay();
      clearTimeout(this.resumeTimer);
      this.resumeTimer = window.setTimeout(() => this.maybeStartAutoplay(), RESUME_AFTER_INTERACTION_MS);
    }

    getRequiredWidth() {
      const styles = window.getComputedStyle(this.features);
      const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
      const itemsWidth = this.items.reduce((total, item) => total + item.getBoundingClientRect().width, 0);
      return itemsWidth + gap * Math.max(this.items.length - 1, 0);
    }

    shouldEnableSlider() {
      if (isMobileLayout()) return false;
      return this.getRequiredWidth() > this.features.clientWidth + 1;
    }

    syncMode() {
      const shouldEnable = this.shouldEnableSlider();
      this.features.classList.toggle(SLIDER_CLASS, shouldEnable);

      if (!shouldEnable) {
        this.stopAutoplay();
        this.features.scrollLeft = 0;
        this.activeIndex = 0;
        return;
      }

      this.activeIndex = Math.min(this.activeIndex, this.items.length - 1);
      this.maybeStartAutoplay();
    }

    maybeStartAutoplay() {
      if (!this.features.classList.contains(SLIDER_CLASS)) return;
      if (this.autoplayTimer) return;
      if (!this.isVisible || prefersReducedMotion()) return;
      this.autoplayTimer = window.setInterval(() => this.advance(), AUTOPLAY_MS);
    }

    stopAutoplay() {
      if (!this.autoplayTimer) return;
      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }

    getNearestIndex() {
      const currentLeft = this.features.scrollLeft;
      let nearestIndex = 0;
      let nearestDistance = Number.POSITIVE_INFINITY;

      this.items.forEach((item, index) => {
        const distance = Math.abs(item.offsetLeft - currentLeft);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestIndex = index;
        }
      });

      return nearestIndex;
    }

    scrollToIndex(index) {
      const target = this.items[index];
      if (!target) return;
      this.features.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
      this.activeIndex = index;
    }

    advance() {
      if (!this.features.classList.contains(SLIDER_CLASS)) {
        this.stopAutoplay();
        return;
      }

      const nextIndex = this.activeIndex >= this.items.length - 1 ? 0 : this.activeIndex + 1;
      this.scrollToIndex(nextIndex);
    }

    destroy() {
      this.stopAutoplay();
      clearTimeout(this.resumeTimer);
      clearTimeout(this.scrollEndTimer);
      this.resizeObserver?.disconnect();
      this.visibilityObserver?.disconnect();
      window.removeEventListener('resize', this.handleResize);
    }
  }

  const instances = new WeakMap();

  function init(root = document) {
    root.querySelectorAll(SECTION_SELECTOR).forEach((section) => {
      if (instances.has(section)) return;
      instances.set(section, new AboutUsLivePerformanceSlider(section));
    });
  }

  function destroySection(section) {
    const instance = instances.get(section);
    if (!instance) return;
    instance.destroy();
    instances.delete(section);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll(SECTION_SELECTOR).forEach((section) => destroySection(section));
  });
})();
